// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/useAskV2Transport.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/createAskV2Transport.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mapServerMessages.test.ts
// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/mergeDisplayMessages.test.ts
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import {
  type AskPunttChat,
  type AskPunttMessage,
  type GetAskPunttChatHistoryResponse,
} from "@mg/schemas/src/christo/askPuntt";
import { type Query } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  createAskV2Transport,
  type AskV2Transport,
} from "./createAskV2Transport";
import { mapServerMessages, nextAskV2HistoryPollMs } from "./mapServerMessages";
import { mergeDisplayMessages } from "./mergeDisplayMessages";
import { type AskV2TransportSnapshot } from "./types";

import { useAskPunttChatHistory } from "../../utils/queries/askPuntt";
import { generateUUID } from "../../utils/uuid";
import {
  abortAskV2Message,
  createAskV2Chat,
  regenerateAskV2Message,
  sendAskV2Message,
} from "../services/chat";

export type UseAskV2TransportOptions = {
  chatId?: string | null;
  enabled?: boolean;
  presentation?: "chat" | "feed";
};

export type AskV2TransportApi = AskV2TransportSnapshot & {
  setComposer: AskV2Transport["setComposer"];
  setWebSearch: AskV2Transport["setWebSearch"];
  addQuotedContext: AskV2Transport["addQuotedContext"];
  removeQuotedContext: AskV2Transport["removeQuotedContext"];
  clearQuotedContexts: AskV2Transport["clearQuotedContexts"];
  setCommentContext: AskV2Transport["setCommentContext"];
  removeCommentContext: AskV2Transport["removeCommentContext"];
  clearCommentContexts: AskV2Transport["clearCommentContexts"];
  send: AskV2Transport["send"];
  abort: AskV2Transport["abort"];
  retry: AskV2Transport["retry"];
  beginEdit: AskV2Transport["beginEdit"];
  setEditDraft: AskV2Transport["setEditDraft"];
  cancelEdit: AskV2Transport["cancelEdit"];
  submitEdit: AskV2Transport["submitEdit"];
  historyLoading: boolean;
  historyError: boolean;
  chatTitle?: string;
  historyChat?: AskPunttChat;
  historyMessages: AskPunttMessage[];
  displayMessages: AskPunttMessage[];
};

export function useAskV2Transport(
  options?: UseAskV2TransportOptions,
): AskV2TransportApi {
  const routeChatId = options?.chatId ?? null;
  const presentation = options?.presentation ?? "chat";

  const transportRef = useRef<AskV2Transport | null>(null);
  if (transportRef.current == null) {
    transportRef.current = createAskV2Transport({
      id: generateUUID,
      initialChatId: routeChatId,
      api: {
        createChat: ({
          message,
          attachments,
          quotedContexts,
          commentContexts,
          mentions,
          mentionEmails,
          webSearch,
        }) =>
          createAskV2Chat(
            message,
            attachments,
            quotedContexts,
            commentContexts,
            mentions,
            mentionEmails,
            webSearch,
          ),
        sendMessage: ({
          chatId: activeChatId,
          message,
          attachments,
          quotedContexts,
          commentContexts,
          mentions,
          mentionEmails,
          webSearch,
        }) =>
          sendAskV2Message(
            activeChatId,
            message,
            attachments,
            quotedContexts,
            commentContexts,
            mentions,
            mentionEmails,
            webSearch,
          ),
        abortMessage: ({ chatId: activeChatId, messageId }) =>
          abortAskV2Message(activeChatId, messageId),
        regenerateMessage: ({ chatId: activeChatId, messageId, content }) =>
          regenerateAskV2Message(activeChatId, messageId, content),
      },
    });
  }

  const transport = transportRef.current;
  const [snapshot, setSnapshot] = useState(transport.getSnapshot);
  const lastSyncRef = useRef<{ chatId: string | null; updatedAt: number }>({
    chatId: null,
    updatedAt: 0,
  });
  const pollStartedAtRef = useRef<number | null>(null);
  const wasInFlightRef = useRef(false);

  const chatId = routeChatId ?? snapshot.chatId;
  const enabled = options?.enabled !== false && !!chatId;

  useEffect(() => {
    if (!routeChatId) return;
    if (transport.getSnapshot().chatId === routeChatId) return;
    // why: project routes learn sharedChatId after load — bind before history
    // returns so send() posts into the shared chat instead of createChat().
    transport.hydrateFromServer({ chatId: routeChatId, messages: [] });
    lastSyncRef.current = { chatId: null, updatedAt: 0 };
    pollStartedAtRef.current = null;
  }, [routeChatId, transport]);

  const historyQuery = useAskPunttChatHistory(chatId, {
    enabled,
    refetchInterval: (query: Query<GetAskPunttChatHistoryResponse, Error>) => {
      const inFlight = transport.getSnapshot().inFlight;
      if (inFlight && !wasInFlightRef.current) {
        pollStartedAtRef.current = null;
      }
      wasInFlightRef.current = inFlight;
      const next = nextAskV2HistoryPollMs({
        queryStatus: query.state.status,
        messages: query.state.data?.messages ?? [],
        inFlight,
        pollStartedAt: pollStartedAtRef.current,
        now: Date.now(),
        agent: query.state.data?.chat?.agent,
      });
      pollStartedAtRef.current = next.pollStartedAt;
      return next.interval;
    },
  });

  useEffect(() => transport.subscribe(setSnapshot), [transport]);

  useEffect(() => {
    if (!enabled || !chatId) {
      if (lastSyncRef.current.chatId) {
        transport.reset();
        lastSyncRef.current = { chatId: null, updatedAt: 0 };
      }
      return;
    }

    if (!historyQuery.data) return;

    const mapped = mapServerMessages(historyQuery.data.messages, presentation);
    if (lastSyncRef.current.chatId !== chatId) {
      // why: hydrateFromServer replaces the thread wholesale. That is right for
      // a chat the transport is not bound to yet, but wrong for the FIRST
      // payload of the chat it is already on: the composer renders while
      // history is still loading, so a turn sent in that window is an
      // optimistic sending/failed row the server has not echoed back yet. A
      // blind hydrate drops it — the bubble vanishes mid-send, the ACK can no
      // longer find its clientId to mark sent, and a failed send loses its
      // retry affordance. syncFromServer keeps that local tail.
      if (transport.getSnapshot().chatId === chatId) {
        transport.syncFromServer(mapped);
      } else {
        transport.hydrateFromServer({ chatId, messages: mapped });
      }
    } else if (historyQuery.dataUpdatedAt !== lastSyncRef.current.updatedAt) {
      transport.syncFromServer(mapped);
    }

    lastSyncRef.current = {
      chatId,
      updatedAt: historyQuery.dataUpdatedAt,
    };
  }, [
    chatId,
    enabled,
    historyQuery.data,
    historyQuery.dataUpdatedAt,
    presentation,
    transport,
  ]);

  const displayMessages = useMemo(
    () =>
      mergeDisplayMessages({
        historyMessages: historyQuery.data?.messages ?? [],
        transportMessages: snapshot.messages,
        chatId: chatId ?? "",
      }),
    [chatId, historyQuery.data?.messages, snapshot.messages],
  );

  return {
    ...snapshot,
    setComposer: transport.setComposer,
    setWebSearch: transport.setWebSearch,
    addQuotedContext: transport.addQuotedContext,
    removeQuotedContext: transport.removeQuotedContext,
    clearQuotedContexts: transport.clearQuotedContexts,
    setCommentContext: transport.setCommentContext,
    removeCommentContext: transport.removeCommentContext,
    clearCommentContexts: transport.clearCommentContexts,
    send: transport.send,
    abort: transport.abort,
    retry: transport.retry,
    beginEdit: transport.beginEdit,
    setEditDraft: transport.setEditDraft,
    cancelEdit: transport.cancelEdit,
    submitEdit: transport.submitEdit,
    historyLoading: enabled && historyQuery.isLoading,
    historyError: enabled && historyQuery.isError,
    chatTitle: historyQuery.data?.chat?.title,
    historyChat: historyQuery.data?.chat,
    historyMessages: historyQuery.data?.messages ?? [],
    displayMessages,
  };
}
